// Normalizers shared by both importers. Pure functions, fully tested.

// ---------------------------------------------------------------------------
// Names used as match keys (companies, practice DBAs)
// ---------------------------------------------------------------------------

const COMPANY_SUFFIXES = new Set([
  'pc', 'pa', 'pllc', 'llc', 'llp', 'lp', 'inc', 'incorporated', 'corp', 'corporation', 'co', 'ltd', 'plc', 'the',
]);

/**
 * Lowercase, '&' -> 'and', punctuation dropped, legal suffixes (PC, LLC, Inc, "The") removed.
 * "The Breast Center, P.C." and "breast center" normalize the same.
 */
export function normalizeCompanyName(name: string | null | undefined): string {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .replace(/&/g, ' and ')
    // Joiners vanish so "P.C." = "pc" and "OB-GYN" = "OB/GYN" = "obgyn".
    .replace(/[.'’`/-]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((w) => w && !COMPANY_SUFFIXES.has(w))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Websites
// ---------------------------------------------------------------------------

/** "https://www.TheHowardCenter.com/locations" -> "thehowardcenter.com". Null for blanks and non-URLs. */
export function websiteDomain(url: string | null | undefined): string | null {
  if (!url) return null;
  let text = String(url).trim();
  if (!text) return null;
  if (!/^[a-z]+:\/\//i.test(text)) text = `https://${text}`;
  try {
    const host = new URL(text).hostname.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
    return host.includes('.') ? host : null;
  } catch {
    return null;
  }
}

/** A website that always opens as a link: adds https:// when missing. */
export function normalizeWebsite(url: string | null | undefined): string | null {
  if (!url) return null;
  const text = String(url).trim();
  if (!text) return null;
  return /^[a-z]+:\/\//i.test(text) ? text : `https://${text}`;
}

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmail(value: unknown): { email: string | null; problem?: string } {
  if (value === null || value === undefined) return { email: null };
  const text = String(value).trim();
  if (!text) return { email: null };
  if (!EMAIL_RE.test(text)) return { email: null, problem: `not a valid email: "${text}"` };
  return { email: text.toLowerCase() };
}

// ---------------------------------------------------------------------------
// Phones
// ---------------------------------------------------------------------------

export interface NormalizedPhone {
  e164: string;
  extension: string | null;
  raw: string;
}

/**
 * US numbers to E.164. Handles bare integers ("7063531750"), a leading 1
 * ("17063531750"), formatted text ("(706) 353-1750 x12"), and Monday's
 * extension-glued-on numbers ("2123195800120" -> +12123195800 ext 120).
 */
export function normalizePhone(value: unknown): { phone: NormalizedPhone | null; problem?: string } {
  if (value === null || value === undefined) return { phone: null };
  const raw = typeof value === 'number' ? String(Math.trunc(value)) : String(value).trim();
  if (!raw) return { phone: null };

  let main = raw;
  let extension: string | null = null;
  const extMatch = raw.match(/^(.*?)(?:\s*(?:x|ext\.?|extension|#)\s*)(\d{1,6})\s*$/i);
  if (extMatch) {
    main = extMatch[1]!;
    extension = extMatch[2]!;
  }
  let digits = main.replace(/\D/g, '');
  if (raw.trim().startsWith('+') && !digits.startsWith('1')) {
    return digits.length >= 8 && digits.length <= 15
      ? { phone: { e164: `+${digits}`, extension, raw } }
      : { phone: null, problem: `unrecognized phone "${raw}"` };
  }
  if (digits.length > 11 && extension === null) {
    // Extension glued on with no separator.
    const national = digits.startsWith('1') ? 11 : 10;
    extension = digits.slice(national);
    digits = digits.slice(0, national);
  }
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10 || !/^[2-9]/.test(digits)) {
    return { phone: null, problem: `unrecognized phone "${raw}"` };
  }
  return { phone: { e164: `+1${digits}`, extension, raw } };
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

const NAME_SUFFIX_RE = /(?:,?\s+|,\s*)(jr\.?|sr\.?|ii|iii|iv|md|m\.d\.|phd|ph\.d\.|do|d\.o\.|dds|dmd|np|pa-c|rn)$/i;
// "Dr. Jane", "Dr Jane" and "Dr.Jane" — but never the "Dr" in "Drew".
const TITLE_PREFIX_RE = /^(?:(dr|mr|mrs|ms)\.\s*|(dr|mr|mrs|ms|miss)\s+)/i;

export interface PersonName {
  first: string | null;
  last: string | null;
  suffix: string | null;
  /** Leading title found in the name, e.g. "Dr." */
  prefix: string | null;
  /** True when the name could not be split into first and last. */
  incomplete: boolean;
}

/**
 * "Dr. Nathan Cleaver" -> Nathan / Cleaver (prefix "Dr."). "Mazen Abdalla,MD" ->
 * Mazen / Abdalla (suffix "MD"). Splits on the last space, per the spec.
 */
export function splitPersonName(value: string | null | undefined): PersonName {
  let text = String(value ?? '').replace(/\s+/g, ' ').trim();
  let prefix: string | null = null;
  let suffix: string | null = null;

  const p = text.match(TITLE_PREFIX_RE);
  if (p) {
    const title = (p[1] ?? p[2])!;
    prefix = /^dr$/i.test(title) ? 'Dr.' : title;
    text = text.slice(p[0].length).trim();
  }
  // Up to two suffixes: "John Smith Jr., MD"
  for (let i = 0; i < 2; i++) {
    const s = text.match(NAME_SUFFIX_RE);
    if (!s) break;
    suffix = suffix ? `${s[1]} ${suffix}` : s[1]!;
    text = text.slice(0, s.index).replace(/[,\s]+$/, '').trim();
  }
  if (suffix) suffix = suffix.replace(/^md$/i, 'MD').replace(/^m\.d\.$/i, 'MD').replace(/^(jr|sr)$/i, (m) => `${m[0]!.toUpperCase()}${m.slice(1).toLowerCase()}.`);

  const space = text.lastIndexOf(' ');
  if (!text) return { first: null, last: null, suffix, prefix, incomplete: true };
  if (space === -1) return { first: text, last: null, suffix, prefix, incomplete: true };
  return { first: text.slice(0, space), last: text.slice(space + 1), suffix, prefix, incomplete: false };
}

// ---------------------------------------------------------------------------
// Addresses and building keys
// ---------------------------------------------------------------------------

/** Practice Finder's zip5(): first 5 digits, zero-filled. */
export function zip5(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const digits = String(value).replace(/\D/g, '');
  const five = digits.slice(0, 5);
  return five ? five.padStart(5, '0') : null;
}

/** Practice Finder's house_number(): the leading digits only ("1860-B" -> "1860"). */
export function houseNumber(street: string | null | undefined): string | null {
  const m = String(street ?? '').match(/^\s*(\d+)/);
  return m ? m[1]! : null;
}

/** Practice Finder's building_key(): "house|zip5", or null if either part is missing. */
export function buildingKey(street: string | null | undefined, zip: unknown): string | null {
  const house = houseNumber(street);
  const z = zip5(zip);
  return house && z ? `${house}|${z}` : null;
}

/** Fallback match for buildings without a key: normalized street + ZIP (or city). */
export function addressMatchKey(street: string | null | undefined, zipOrCity: string | null | undefined): string | null {
  const s = String(street ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const place = String(zipOrCity ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  return s && place ? `${s}|${place}` : null;
}

const US_STATES = new Set(
  'AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC'.split(' '),
);

export interface ParsedAddress {
  street: string | null;
  city: string | null;
  state: string | null;
  zip5: string | null;
  raw: string;
  /** False when a part is missing or looks wrong (e.g. a 4-digit ZIP); kept and flagged, never dropped. */
  complete: boolean;
  problems: string[];
}

/**
 * Parses Monday's free-text addresses: "3840 Peachtree Industrial Blvd, Duluth, GA 30096".
 * Tolerates missing state or ZIP, trailing commas and a missing comma before the city.
 * Returns null for strings with no address content (", ,").
 */
export function parseAddress(value: unknown): ParsedAddress | null {
  const raw = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!raw.replace(/[\s,]/g, '')) return null;
  const problems: string[] = [];

  let text = raw.replace(/,\s*Property\s*\d+\s*$/i, '');
  let zip: string | null = null;
  const zipMatch = text.match(/\b(\d{4,5})(?:-\d{4})?\s*,?\s*$/);
  if (zipMatch && /[A-Za-z]/.test(text.slice(0, zipMatch.index))) {
    if (zipMatch[1]!.length === 4) problems.push(`ZIP "${zipMatch[1]}" has only 4 digits`);
    else zip = zipMatch[1]!;
    text = text.slice(0, zipMatch.index);
  }
  text = text.replace(/[\s,]+$/, '');

  let state: string | null = null;
  const stateMatch = text.match(/(?:,|\s)\s*([A-Za-z]{2})$/);
  if (stateMatch && US_STATES.has(stateMatch[1]!.toUpperCase())) {
    state = stateMatch[1]!.toUpperCase();
    text = text.slice(0, stateMatch.index).replace(/[\s,]+$/, '');
  }

  const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
  let street: string | null = parts[0] ?? null;
  let city: string | null = parts.length > 1 ? parts.slice(1).join(', ') : null;
  if (city === null && street) {
    // "102 Main Street Sharpsburg" — no comma before the city. Split after the last street type.
    const m = street.match(/^(.*\b(?:st|street|rd|road|ave|avenue|blvd|boulevard|dr|drive|pkwy|parkway|hwy|highway|ln|lane|ct|court|cir|circle|way|pl|place|trace|trl|trail|sq|square|ter|terrace)\.?)\s+([A-Za-z][A-Za-z .'-]+)$/i);
    if (m) {
      street = m[1]!;
      city = m[2]!;
    }
  }
  if (city) city = city.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\B\w+/g, (w) => w.toLowerCase());

  if (!street || !houseNumber(street)) problems.push('no street number');
  if (!city) problems.push('no city');
  if (!state) problems.push('no state');
  if (!zip && !problems.some((p) => p.startsWith('ZIP'))) problems.push('no ZIP');

  return { street, city, state, zip5: zip, raw, complete: problems.length === 0, problems };
}
